import { useState, useEffect } from "react";
import { useAuth } from "../App";
import { Layout } from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import {
  BarChart3,
  Download,
  GraduationCap,
  Users,
  Filter,
  FileSpreadsheet
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ReportsPage = () => {
  const { chapter } = useAuth();
  const [activeTab, setActiveTab] = useState("training");
  const [trainingReport, setTrainingReport] = useState(null);
  const [visitorReport, setVisitorReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [visitorFilters, setVisitorFilters] = useState({ status: "all", interest_level: "all" });

  const token = localStorage.getItem("token");
  const primaryColor = chapter?.primary_color || "#CC0000";

  useEffect(() => { fetchTrainingReport(); fetchVisitorReport(); }, []);

  const fetchTrainingReport = async () => {
    try {
      const res = await fetch(`${API}/reports/training`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) setTrainingReport(await res.json());
    } catch (error) {
      console.error("Training report error:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchVisitorReport = async () => {
    try {
      let url = `${API}/reports/visitors?`;
      if (visitorFilters.status !== "all") url += `status=${visitorFilters.status}&`;
      if (visitorFilters.interest_level !== "all") url += `interest_level=${visitorFilters.interest_level}&`;

      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) setVisitorReport(await res.json());
    } catch (error) {
      console.error("Visitor report error:", error);
    }
  };

  useEffect(() => { fetchVisitorReport(); }, [visitorFilters]);

  const handleExport = async (type) => {
    try {
      let url = `${API}/reports/export/${type}`;
      if (type === "visitors") {
        const params = new URLSearchParams();
        if (visitorFilters.status !== "all") params.append("status", visitorFilters.status);
        if (visitorFilters.interest_level !== "all") params.append("interest_level", visitorFilters.interest_level);
        if (params.toString()) url += `?${params}`;
      }

      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const blob = await res.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = downloadUrl;
        a.download = `${type}_report.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(downloadUrl);
        toast.success(`${type} report exported`);
      } else {
        toast.error("Export failed");
      }
    } catch (error) {
      toast.error("Error exporting report");
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="reports-page">
        <div>
          <h1 className="text-3xl font-display font-bold text-slate-900">Reports</h1>
          <p className="text-muted-foreground mt-1">View and export chapter reports</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b">
          <button
            onClick={() => setActiveTab("training")}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === "training" ? "border-current" : "border-transparent text-muted-foreground hover:text-slate-700"
            }`}
            style={activeTab === "training" ? { color: primaryColor, borderColor: primaryColor } : {}}
            data-testid="tab-training-report"
          >
            <GraduationCap className="h-4 w-4 inline mr-2" />
            Training Report
          </button>
          <button
            onClick={() => setActiveTab("visitors")}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === "visitors" ? "border-current" : "border-transparent text-muted-foreground hover:text-slate-700"
            }`}
            style={activeTab === "visitors" ? { color: primaryColor, borderColor: primaryColor } : {}}
            data-testid="tab-visitor-report"
          >
            <Users className="h-4 w-4 inline mr-2" />
            Visitor Report
          </button>
        </div>

        {/* Training Report */}
        {activeTab === "training" && trainingReport && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="text-sm text-muted-foreground">
                {trainingReport.total_members} members, {trainingReport.total_topics} training topics
              </div>
              <Button variant="outline" onClick={() => handleExport("training")} data-testid="export-training-btn">
                <Download className="mr-2 h-4 w-4" /> Export CSV
              </Button>
            </div>

            <div className="space-y-3">
              {trainingReport.report?.sort((a, b) => b.progress_pct - a.progress_pct).map(m => (
                <Card key={m.member_id} className="border-0 shadow-sm" data-testid={`training-row-${m.member_id}`}>
                  <CardContent className="py-4">
                    <div className="flex items-center gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3">
                          <div
                            className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0"
                            style={{ backgroundColor: primaryColor }}
                          >
                            {m.full_name?.charAt(0)?.toUpperCase()}
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium text-sm truncate">{m.full_name}</p>
                            <p className="text-xs text-muted-foreground">{m.company}</p>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-4 flex-shrink-0">
                        <div className="w-40">
                          <Progress value={m.progress_pct} className="h-2" />
                        </div>
                        <Badge
                          variant={m.progress_pct === 100 ? "default" : "outline"}
                          className={m.progress_pct === 100 ? "bg-green-100 text-green-700" : ""}
                        >
                          {m.completed}/{m.total_topics} ({m.progress_pct}%)
                        </Badge>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {/* Visitor Report */}
        {activeTab === "visitors" && (
          <div className="space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-3">
                <Filter className="h-4 w-4 text-muted-foreground" />
                <Select value={visitorFilters.status} onValueChange={(v) => setVisitorFilters(prev => ({ ...prev, status: v }))}>
                  <SelectTrigger className="w-[150px]">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="Active">Active</SelectItem>
                    <SelectItem value="Converted">Converted</SelectItem>
                    <SelectItem value="Inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={visitorFilters.interest_level} onValueChange={(v) => setVisitorFilters(prev => ({ ...prev, interest_level: v }))}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Interest Level" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Interest Levels</SelectItem>
                    <SelectItem value="1">Level 1 (Interested)</SelectItem>
                    <SelectItem value="2">Level 2 (Needs Permission)</SelectItem>
                    <SelectItem value="3">Level 3 (Not Now)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button variant="outline" onClick={() => handleExport("visitors")} data-testid="export-visitors-btn">
                <Download className="mr-2 h-4 w-4" /> Export CSV
              </Button>
            </div>

            {/* Summary cards */}
            {visitorReport?.summary && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <Card className="border-0 shadow-sm">
                  <CardContent className="pt-6 text-center">
                    <p className="text-2xl font-bold" style={{ color: primaryColor }}>{visitorReport.summary.total}</p>
                    <p className="text-xs text-muted-foreground mt-1">Total Visitors</p>
                  </CardContent>
                </Card>
                {Object.entries(visitorReport.summary.by_status || {}).map(([s, count]) => (
                  <Card key={s} className="border-0 shadow-sm">
                    <CardContent className="pt-6 text-center">
                      <p className="text-2xl font-bold">{count}</p>
                      <p className="text-xs text-muted-foreground mt-1">{s}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {/* Visitor table */}
            {visitorReport?.visitors?.length > 0 ? (
              <Card className="border-0 shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-slate-50">
                        <th className="text-left p-3 font-medium text-slate-600">Name</th>
                        <th className="text-left p-3 font-medium text-slate-600">Company</th>
                        <th className="text-left p-3 font-medium text-slate-600">Email</th>
                        <th className="text-left p-3 font-medium text-slate-600">Status</th>
                        <th className="text-left p-3 font-medium text-slate-600">Interest</th>
                        <th className="text-left p-3 font-medium text-slate-600">Visit Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visitorReport.visitors.map(v => (
                        <tr key={v.visitor_id} className="border-b hover:bg-slate-50" data-testid={`visitor-row-${v.visitor_id}`}>
                          <td className="p-3 font-medium">{v.full_name}</td>
                          <td className="p-3 text-muted-foreground">{v.company || "-"}</td>
                          <td className="p-3 text-muted-foreground">{v.email || "-"}</td>
                          <td className="p-3">
                            <Badge className={
                              v.status === "Active" ? "bg-green-100 text-green-700" :
                              v.status === "Converted" ? "bg-blue-100 text-blue-700" :
                              "bg-slate-100 text-slate-700"
                            }>{v.status}</Badge>
                          </td>
                          <td className="p-3">
                            <Badge variant="outline">Level {v.interest_level || "-"}</Badge>
                          </td>
                          <td className="p-3 text-muted-foreground">{v.visit_date || v.first_visit_date || "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            ) : (
              <Card className="border-0 shadow-sm">
                <CardContent className="py-12 text-center">
                  <FileSpreadsheet className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">No visitor data matching filters</p>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default ReportsPage;
